import { keepPreviousData, QueryClient } from '@tanstack/react-query';

import { isNetworkFailure, isRequestTimeout } from '@/domain/errors';

/**
 * Keep the last successful list result on screen while a filter, tab, or page
 * change fetches the next one. Without this, a new query key makes `isPending`
 * true and DataTable swaps the table for a loader.
 */
export const keepPreviousListData = keepPreviousData;

/** Same budget as TanStack Query's default `retry: 3` — for 5xx, not transport failures. */
const QUERY_RETRY_LIMIT = 3;

/**
 * Network failures must not retry. Query's default `retry: 3` plus exponential
 * backoff (~7s) is why "No connection" appeared long after the first Failed to
 * fetch, and a retry while `navigator` is offline pauses forever (loader, no
 * error) because `networkMode: 'online'` waits for `onlineManager`.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  // A timeout is one wait, then the timeout message. Replaying it three times
  // only stretches the spinner; a timed-out write must not be sent again.
  if (isNetworkFailure(error) || isRequestTimeout(error)) return false;
  return failureCount < QUERY_RETRY_LIMIT;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        networkMode: 'always',
        retry: shouldRetryQuery,
      },
      mutations: {
        networkMode: 'always',
      },
    },
  });
}
